import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertTriangle, CheckCircle2, Search, ShieldAlert, Trash2, Package, Clock, Filter,
  FileBarChart, ChevronLeft, ChevronRight, Loader2, Download, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtCurrency } from '../lib/localeUtils';
import { exportCSV, exportPDF } from '../lib/reportExportUtils';
import api from '../lib/api';
import { keyed } from '../lib/listKeys';

export default function ExpiredProductsPage() {
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' | 'audit'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, not_removed, removed
  const [stats, setStats] = useState({ total: 0, not_removed_count: 0 });
  const [processing, setProcessing] = useState(null);

  // Audit report state
  const [auditRecords, setAuditRecords] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditPage, setAuditPage] = useState(1);
  const [auditPages, setAuditPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSummary, setAuditSummary] = useState({ total_items_removed: 0, total_cost_value: 0, total_selling_value: 0, total_loss: 0 });

  const fetchExpired = useCallback(async () => {
    try {
      const res = await api.get('/expired-products');
      setItems(res.data.expired_items || []);
      setStats({ total: res.data.total, not_removed_count: res.data.not_removed_count });
    } catch (e) {
      toast.error('Failed to load expired products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExpired(); }, [fetchExpired]);

  const fetchAuditReport = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = { page: auditPage, limit: 20 };
      if (auditSearch) params.search = auditSearch;
      if (auditDateFrom) params.date_from = auditDateFrom;
      if (auditDateTo) params.date_to = auditDateTo;
      const res = await api.get('/expired-products/audit-report', { params });
      setAuditRecords(res.data.records || []);
      setAuditTotal(res.data.total || 0);
      setAuditPages(res.data.pages || 1);
      setAuditSummary(res.data.summary || { total_items_removed: 0, total_cost_value: 0, total_selling_value: 0, total_loss: 0 });
    } catch (e) {
      toast.error('Failed to load audit report');
    } finally {
      setAuditLoading(false);
    }
  }, [auditPage, auditSearch, auditDateFrom, auditDateTo]);

  useEffect(() => {
    if (activeTab === 'audit') {
      const t = setTimeout(fetchAuditReport, 300);
      return () => clearTimeout(t);
    }
  }, [activeTab, fetchAuditReport]);

  const handleMarkRemoved = async (productId, batchNumber, productName) => {
    setProcessing(`${productId}-${batchNumber}`);
    try {
      const res = await api.post(`/expired-products/${productId}/mark-removed`, {
        batch_number: batchNumber,
        notes: 'Marked via Expired Products page'
      });
      toast.success(res.data.message);
      fetchExpired();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark as removed');
    } finally {
      setProcessing(null);
    }
  };

  const filtered = items.filter(item => {
    const matchesSearch = !search || item.product_name.toLowerCase().includes(search.toLowerCase()) || item.batch_number.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || (filter === 'not_removed' && !item.removed_from_shelf) || (filter === 'removed' && item.removed_from_shelf);
    return matchesSearch && matchesFilter;
  });

  const getDaysExpired = (expiryDate) => {
    const exp = new Date(expiryDate);
    const now = new Date();
    return Math.floor((now - exp) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="expired-products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-500" />
            Expired Products
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage expired batches and view audit reports.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'manage' ? 'default' : 'outline'}
            onClick={() => setActiveTab('manage')}
            className="gap-1.5"
            data-testid="tab-manage"
          >
            <Package className="h-4 w-4" /> Manage
          </Button>
          <Button
            variant={activeTab === 'audit' ? 'default' : 'outline'}
            onClick={() => setActiveTab('audit')}
            className="gap-1.5"
            data-testid="tab-audit-report"
          >
            <FileBarChart className="h-4 w-4" /> Audit Report
          </Button>
        </div>
      </div>

      {activeTab === 'manage' && (<>
      {/* Alert Banner */}
      {stats.not_removed_count > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3" data-testid="expiry-alert-banner">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-500">
              {stats.not_removed_count} expired batch{stats.not_removed_count > 1 ? 'es' : ''} still on shelves!
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              These items must be physically removed from shelves and marked below to prevent accidental sale of expired medicine.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Expired</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <Package className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-500">{stats.not_removed_count}</p>
              <p className="text-xs text-muted-foreground">On Shelves</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-500">{stats.total - stats.not_removed_count}</p>
              <p className="text-xs text-muted-foreground">Removed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by product name or batch number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="expired-search"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'not_removed', 'removed'].map(f => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                >
                  <Filter className="h-3 w-3 mr-1" />
                  {f === 'all' ? 'All' : f === 'not_removed' ? 'On Shelf' : 'Removed'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Expired Batches ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                {stats.total === 0 ? 'No expired products found' : 'No items match your filter'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-3 px-2 font-medium">Product</th>
                    <th className="text-left py-3 px-2 font-medium">Batch</th>
                    <th className="text-left py-3 px-2 font-medium">Expired On</th>
                    <th className="text-center py-3 px-2 font-medium">Days Expired</th>
                    <th className="text-center py-3 px-2 font-medium">Qty</th>
                    <th className="text-center py-3 px-2 font-medium">Status</th>
                    <th className="text-center py-3 px-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => {
                    const days = getDaysExpired(item.expiry_date);
                    const isProcessing = processing === `${item.product_id}-${item.batch_number}`;
                    return (
                      <tr
                        key={`${item.product_id}-${item.batch_number}`}
                        className={`border-b transition-colors ${!item.removed_from_shelf ? 'bg-red-500/5 hover:bg-red-500/10' : 'hover:bg-muted/50'}`}
                        data-testid={`expired-row-${item.batch_number}`}
                      >
                        <td className="py-3 px-2">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-xs text-muted-foreground">{item.category}</p>
                        </td>
                        <td className="py-3 px-2 font-mono text-xs">{item.batch_number}</td>
                        <td className="py-3 px-2">{item.expiry_date}</td>
                        <td className="py-3 px-2 text-center">
                          <Badge variant={days > 30 ? 'destructive' : 'secondary'} className="text-xs">
                            {days}d ago
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center font-medium">{item.quantity}</td>
                        <td className="py-3 px-2 text-center">
                          {item.removed_from_shelf ? (
                            <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Removed
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs gap-1">
                              <AlertTriangle className="h-3 w-3" /> On Shelf
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {!item.removed_from_shelf && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleMarkRemoved(item.product_id, item.batch_number, item.product_name)}
                              disabled={isProcessing}
                              data-testid={`mark-removed-${item.batch_number}`}
                              className="text-xs gap-1"
                            >
                              <Trash2 className="h-3 w-3" />
                              {isProcessing ? 'Processing...' : 'Mark Removed'}
                            </Button>
                          )}
                          {item.removed_from_shelf && item.removed_at && (
                            <span className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(item.removed_at).toLocaleDateString()}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Audit Report Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6" data-testid="audit-report-section">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{auditSummary.total_items_removed}</p>
                <p className="text-xs text-muted-foreground mt-1">Units Removed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{fmtCurrency(auditSummary.total_cost_value)}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Cost Value</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-500">{fmtCurrency(auditSummary.total_selling_value)}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Selling Value</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-destructive">{fmtCurrency(auditSummary.total_selling_value - auditSummary.total_cost_value)}</p>
                <p className="text-xs text-muted-foreground mt-1">Lost Margin</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search product or batch..."
                    value={auditSearch}
                    onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(1); }}
                    className="pl-9"
                    data-testid="audit-search"
                  />
                </div>
                <Input
                  type="date"
                  value={auditDateFrom}
                  onChange={(e) => { setAuditDateFrom(e.target.value); setAuditPage(1); }}
                  className="w-40"
                  placeholder="From"
                  data-testid="audit-date-from"
                />
                <Input
                  type="date"
                  value={auditDateTo}
                  onChange={(e) => { setAuditDateTo(e.target.value); setAuditPage(1); }}
                  className="w-40"
                  placeholder="To"
                  data-testid="audit-date-to"
                />
              </div>
            </CardContent>
          </Card>

          {/* Audit Table — Read Only */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileBarChart className="h-4 w-4" />
                Expired Products Removal Log ({auditTotal})
              </CardTitle>
              {auditRecords.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => {
                      const rows = auditRecords.map(r => ({
                        product_name: r.product_name,
                        batch_number: r.batch_number,
                        quantity_removed: r.quantity_removed,
                        unit_cost: r.unit_cost || 0,
                        unit_price: r.unit_price || 0,
                        cost_value: r.cost_value || 0,
                        selling_value: r.selling_value || 0,
                        removed_by: r.removed_by || '',
                        removed_at: r.removed_at ? new Date(r.removed_at).toLocaleString() : '',
                        notes: r.notes || '',
                      }));
                      exportCSV(rows, 'Expired_Products_Audit_Report');
                      toast.success('CSV exported');
                    }}
                    data-testid="audit-export-csv"
                  >
                    <Download className="h-3.5 w-3.5" /> CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => {
                      const rows = auditRecords.map(r => ({
                        product_name: r.product_name,
                        batch_number: r.batch_number,
                        quantity_removed: r.quantity_removed,
                        unit_cost: r.unit_cost || 0,
                        unit_price: r.unit_price || 0,
                        cost_value: r.cost_value || 0,
                        selling_value: r.selling_value || 0,
                        removed_by: r.removed_by || '',
                        removed_at: r.removed_at ? new Date(r.removed_at).toLocaleString() : '',
                        notes: r.notes || '',
                      }));
                      exportPDF(rows, 'Expired_Products_Audit_Report', {
                        total_units_removed: auditSummary.total_items_removed,
                        total_cost_value: auditSummary.total_cost_value,
                        total_selling_value: auditSummary.total_selling_value,
                        lost_margin: auditSummary.total_selling_value - auditSummary.total_cost_value,
                      });
                      toast.success('PDF exported');
                    }}
                    data-testid="audit-export-pdf"
                  >
                    <FileDown className="h-3.5 w-3.5" /> PDF
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : auditRecords.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileBarChart className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No removal records found</p>
                  <p className="text-xs mt-1">Records appear here when expired batches are marked as removed.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Product</TableHead>
                          <TableHead>Batch</TableHead>
                          <TableHead className="text-center">Qty Removed</TableHead>
                          <TableHead className="text-right">Unit Cost</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Cost Value</TableHead>
                          <TableHead className="text-right">Selling Value</TableHead>
                          <TableHead>Removed By</TableHead>
                          <TableHead>Removed At</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {keyed(auditRecords, rec => rec.record_id || `${rec.product_name}-${rec.batch_number}-${rec.removed_at || ''}`).map(({ item: rec, key }, idx) => (
                          <TableRow key={key} data-testid={`audit-row-${idx}`}>
                            <TableCell className="font-medium">{rec.product_name}</TableCell>
                            <TableCell className="font-mono text-xs">{rec.batch_number}</TableCell>
                            <TableCell className="text-center font-semibold">{rec.quantity_removed}</TableCell>
                            <TableCell className="text-right">{fmtCurrency(rec.unit_cost || 0)}</TableCell>
                            <TableCell className="text-right">{fmtCurrency(rec.unit_price || 0)}</TableCell>
                            <TableCell className="text-right text-red-500 font-medium">{fmtCurrency(rec.cost_value || 0)}</TableCell>
                            <TableCell className="text-right text-orange-500 font-medium">{fmtCurrency(rec.selling_value || 0)}</TableCell>
                            <TableCell className="text-sm">{rec.removed_by || '-'}</TableCell>
                            <TableCell className="text-sm">{rec.removed_at ? new Date(rec.removed_at).toLocaleString() : '-'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{rec.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {auditPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <p className="text-xs text-muted-foreground">Page {auditPage} of {auditPages} ({auditTotal} records)</p>
                      <div className="flex gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={auditPage <= 1} onClick={() => setAuditPage(p => p - 1)} data-testid="audit-prev-page">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={auditPage >= auditPages} onClick={() => setAuditPage(p => p + 1)} data-testid="audit-next-page">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
